/**
 * The pieces a member's overview is drawn from: the numbered steps of their
 * first day (§40.5) and the grid of what one join brings on its own (§40.6).
 * The workspace lists take the shared hairline list (components/Section.tsx).
 *
 * They were `FirstDay.tsx`'s own until the settled page needed the same
 * workspace list in the same frame. A second copy would have been two lists
 * drifting apart on the one screen a member opens to see what they have — and
 * these are presentation and nothing else: every one of them is given what to
 * draw and reads nothing.
 */
import type React from 'react';
import { color, font, radius } from '../../theme/tokens';

/** `a`, `a and b`, `a, b and c` (or `a or b` when either one will do) — a list in a sentence, never `a, b, `. */
export function sentenceList(parts: string[], conjunction: 'and' | 'or' = 'and'): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} ${conjunction} ${parts[parts.length - 1]}`;
}

/* ── the redesign's steps (§40.5) and what arrives (§40.6) ─────────────── */

export type StepState = 'todo' | 'current' | 'done';

const STEP_SAYS: Record<StepState, string> = { todo: 'to come', current: 'current', done: 'done' };

/** A check glyph in a given size and colour. */
function Check({ size, stroke }: { size: number; stroke: string }): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: 'none' }}
    >
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

/**
 * One step of a path somebody has to see the whole of — where they are and
 * where it ends (§40.5). The round mark 24 is white ringed #86868B with its
 * mono number while ahead, filled ink while current, #E0F5F3 with a check once
 * done; it doubles a hidden « Step 2 of 3, current », and the current step
 * carries aria-current="step". The header is the title 17 / 600 with its meta
 * on the line, the lede 13 under it and the actions at the right; the body is
 * indented 36 (to the title; 0 on a phone), its blocks 16 apart. A done step
 * folds to its header.
 */
export function Step({
  n,
  of,
  state,
  title,
  meta,
  lede,
  actions,
  children
}: {
  n: number;
  of: number;
  state: StepState;
  title: string;
  meta?: string;
  lede?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <section
      aria-current={state === 'current' ? 'step' : undefined}
      style={{ display: 'grid', gridTemplateColumns: '24px minmax(0, 1fr)', columnGap: 12, alignItems: 'start' }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 24,
          height: 24,
          marginTop: 0,
          boxSizing: 'border-box',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: font.mono,
          fontSize: 12,
          border: state === 'todo' ? `1px solid ${color.lineInput}` : 0,
          background: state === 'current' ? color.ink : state === 'done' ? color.okBg : color.surface,
          color: state === 'current' ? '#FFFFFF' : color.inkMuted
        }}
      >
        {state === 'done' ? <Check size={14} stroke={color.okText} /> : n}
      </span>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', minWidth: 0 }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', lineHeight: '24px' }}>
            <span className="al-sr">{`Step ${n} of ${of}, ${STEP_SAYS[state]}: `}</span>
            <span style={{ fontSize: 17, fontWeight: 600, color: color.ink }}>{title}</span>
            {meta && <span style={{ fontSize: 13, fontWeight: 400, color: color.inkSubtle }}>{meta}</span>}
          </h2>
          {lede && (
            <p style={{ margin: '4px 0 0', maxWidth: 680, fontSize: 13, lineHeight: 1.5, color: color.inkSubtle }}>{lede}</p>
          )}
        </div>
        {actions && <div style={{ flex: 'none', display: 'flex', gap: 8 }}>{actions}</div>}
      </div>
      {state !== 'done' && children && (
        <div
          className="al-step-body"
          style={{ gridColumn: 2, minWidth: 0, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * What an action brings without asking anything (§40.6): a frame on the
 * hairline, radius 14, two equal columns divided by a hairline (one on a
 * phone), each case a check 16 teal, its title 13.5 / 500 and text 13. With an
 * odd count the last case stays white — no grey filler. Not interactive.
 */
export function Arrives({ items }: { items: [string, string][] }): React.JSX.Element {
  return (
    <ul
      className="al-two"
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
        border: `1px solid ${color.line}`,
        borderRadius: radius.xxl,
        overflow: 'hidden',
        background: color.surface
      }}
    >
      {items.map(([what, why]) => (
        <li
          key={what}
          className="al-arrives-case"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '16px 20px',
            // The hairlines between cases are drawn by each case, so an odd
            // last case leaves the frame white rather than a grey gap.
            boxShadow: `-1px -1px 0 ${color.line}`
          }}
        >
          <span style={{ marginTop: 1, display: 'flex' }}>
            <Check size={16} stroke={color.accentText} />
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 500, color: color.ink }}>{what}</span>
            <span style={{ display: 'block', fontSize: 13, lineHeight: 1.45, color: color.inkSubtle, marginTop: 2 }}>
              {why}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
